// ============================================================================
// THE DERIVATIONS behind the Connections screen.
//
// Everything here answers a question the mockups answer with a rendered string,
// and that nothing in the data model is allowed to store: how the header counts
// itself, which single banner the page shows, which colour the settings-nav dot
// takes, which section comes first, and whether a capture token has gone stale.
//
// They live in their own module and not inside the components because the rules
// are the interesting part — "a rate limit is not an error", "the worst state
// wins the dot", "the section carrying the problem comes first" — and rules that
// live in JSX cannot be unit-tested (see model.test.ts).
// ============================================================================

import type {
  CaptureToken,
  Connector,
  DataStatus,
  DesktopWorkerStatus,
  ISODate,
} from '@app/shared'
import { connectorIsLive } from '@app/shared'

import { agoPhrase, daysAgoOf, formatShortDateOf, stampPhrase } from './labels'
import type { MailAccount } from '../../lib/data/types'

export type Tone = 'danger' | 'warning' | 'neutral'

// ── The header's own count line ──────────────────────────────────────────────

/**
 * '2 connected · 1 needs attention · 2 planned'
 * '2 connected · worker offline · 2 planned'
 *
 * A RATE LIMIT IS NOT ATTENTION. Reddit being told to slow down needs nothing
 * from the owner — the retry is already scheduled — so it never appears in this
 * count, and 12b reads "worker offline" rather than "1 needs attention" even
 * though a connector is rate limited on that screen. Only a state with an action
 * behind it gets to ask for attention.
 */
export function headerCounts(connectors: Connector[], worker?: DesktopWorkerStatus): string {
  const connected = connectors.filter(connectorIsLive).length
  const notConnected = connectors.filter((c) => c.status === 'disconnected').length
  const planned = connectors.filter((c) => c.status === 'planned').length
  const errors = connectors.filter((c) => c.status === 'error').length

  const parts = [`${connected} connected`]
  if (errors > 0) parts.push(`${errors} needs attention`)
  if (worker?.state === 'offline') parts.push('worker offline')
  // A built connector nobody has connected yet is neither connected nor planned, and saying so is
  // the only count that does not round it into one of the other two.
  if (notConnected > 0) parts.push(`${notConnected} not connected`)
  parts.push(`${planned} planned`)
  return parts.join(' · ')
}

// ── The one page-level banner ────────────────────────────────────────────────

export interface PageBanner {
  tone: Exclude<Tone, 'neutral'>
  /** Names the consequence, then what still works. Never an error code. */
  text: string
  /** Exactly one action. */
  actionLabel: string
  /**
   * The connector the action retries, or `null` when the banner's action is not a
   * retry. The banner has to offer the SAME action as the card it is about — a
   * banner whose only button does nothing is the complaint this screen exists to
   * prevent — so it carries the id the click needs rather than making the
   * component re-find the broken connector and risk disagreeing with this text.
   */
  actionConnectorId: string | null
}

/**
 * ONE BANNER, THE WORST STATE. A broken connector outranks demo mode: stale
 * coursework is something the owner has to fix, demo mode is something he chose.
 * An offline worker gets NO banner at all — its card already names the queue and
 * the wait, and a banner for a state that resolves itself teaches the owner to
 * ignore banners.
 *
 * THE BROKEN-CONNECTOR ACTION IS A RETRY, NOT A RECONNECT. It used to read
 * "Reconnect Canvas", and that was the app claiming to know something it does not:
 * a connector in `error` has failed, which is not the same as having an expired
 * credential, and production has already produced the counter-example (bCourses
 * answering 406 to a token that is months from expiring). Re-running the sync is
 * the one recovery act that is always honest AND that the owner can perform
 * without a terminal — so it is the one the banner offers, and the connector's own
 * `note` keeps the job of explaining what went wrong.
 */
export function pageBanner(
  connectors: Connector[],
  dataStatus: DataStatus | undefined,
  today: ISODate,
  nowMinutes: number,
): PageBanner | null {
  const broken = connectors.find((c) => c.status === 'error' && c.outage && c.lastSyncAt)
  if (broken) {
    const from = formatShortDateOf(broken.lastSyncAt!)
    return {
      tone: 'danger',
      text:
        `${broken.shortName} stopped syncing ${agoPhrase(broken.lastSyncAt!, today, nowMinutes)}, ` +
        `so ${broken.outage!.stale} are from ${from}. Nothing was lost — it just stopped updating.`,
      actionLabel: `Try ${broken.shortName} again`,
      actionConnectorId: broken.id,
    }
  }

  if (dataStatus?.mode === 'demo') {
    // The count is dropped rather than guessed when the app cannot know it.
    const entries = dataStatus.realJournalEntries
    const yours = entries === null ? 'Your real entries are' : `Your real ${entries} entries are`
    return {
      tone: 'warning',
      text:
        'Demo mode is on — journal entries, courses and saves on every screen are sample data. ' +
        `${yours} untouched and come back the moment you switch it off.`,
      // No connector id: this one is still unwired on purpose. Which dataset the
      // app runs on is a build's configuration, not a button, so there is no click
      // to offer and the sentence on hover is the whole honest answer.
      actionLabel: 'Switch to real data',
      actionConnectorId: null,
    }
  }

  return null
}

// ── The settings-nav dot ─────────────────────────────────────────────────────

/**
 * The dot on the active nav row carries the WORST current state, so the row is
 * readable from the rail without opening the screen: red for something broken,
 * amber for something degraded or pretending, nothing at all when everything is
 * genuinely fine. `null` is a real answer — a dot that is always lit is a dot
 * nobody looks at.
 */
export function worstTone(
  connectors: Connector[] | undefined,
  worker: DesktopWorkerStatus | undefined,
  dataStatus: DataStatus | undefined,
): Tone | null {
  if (connectors?.some((c) => c.status === 'error')) return 'danger'
  if (
    worker?.state === 'offline' ||
    dataStatus?.mode === 'demo' ||
    connectors?.some((c) => c.status === 'rate_limited')
  ) {
    return 'warning'
  }
  return null
}

// ── Which section leads ──────────────────────────────────────────────────────

export type Section = 'sources' | 'machine'

/**
 * THE SECTION CARRYING THE PROBLEM COMES FIRST. 12a leads with SOURCES because
 * Canvas is broken; 12b leads with YOUR MACHINE because the PC is asleep and the
 * connectors are fine. Scrolling to find out what is wrong is the failure this
 * screen exists to prevent.
 */
export function sectionOrder(
  connectors: Connector[] | undefined,
  worker: DesktopWorkerStatus | undefined,
): Section[] {
  const connectorTrouble = connectors?.some((c) => c.status === 'error') ?? false
  if (!connectorTrouble && worker?.state === 'offline') return ['machine', 'sources']
  return ['sources', 'machine']
}

// ── Per-connector phrasing ───────────────────────────────────────────────────

/**
 * '3 days ago · Aug 21, 6:12pm' · '18 min ago · 4 new' · '2 hours ago'.
 *
 * A BROKEN CONNECTOR GETS THE EXACT STAMP as well as the relative age, because
 * "3 days ago" is what you feel and "Aug 21, 6:12pm" is what you check your
 * coursework against. A healthy one gets the new-item count instead: that is the
 * question you actually have of a connector that is working.
 */
export function lastSyncLabel(
  connector: Connector,
  today: ISODate,
  nowMinutes: number,
): string | null {
  if (!connector.lastSyncAt) return null
  const ago = agoPhrase(connector.lastSyncAt, today, nowMinutes)
  if (connector.status === 'error') return `${ago} · ${stampPhrase(connector.lastSyncAt)}`
  if (connector.newItemsLastSync !== null) return `${ago} · ${connector.newItemsLastSync} new`
  return ago
}

/**
 * One Gmail account row's sync line: 'Last sync 12 min ago · 214 messages'.
 *
 * Null when the account has never synced — then nothing about syncing is said at all, because
 * "0 messages" beside an inbox nobody has read yet would be a claim. The count is left off when it
 * could not be read (`messageCount: null`), for the same reason.
 */
export function accountSyncLine(
  account: Pick<MailAccount, 'lastSyncAt' | 'messageCount'>,
  today: ISODate,
  nowMinutes: number,
): string | null {
  if (!account.lastSyncAt) return null
  const ago = `Last sync ${agoPhrase(account.lastSyncAt, today, nowMinutes)}`
  if (account.messageCount === null) return ago
  return `${ago} · ${account.messageCount} ${account.messageCount === 1 ? 'message' : 'messages'}`
}

// ── Capture tokens ───────────────────────────────────────────────────────────

/**
 * A token nobody has used in a month is a key under a doormat. Past this the row
 * greys out and its Revoke turns DARK — the destructive action gets *more*
 * prominent for a stale token, not less (README §12).
 *
 * 30 days, not 83: 83 is what the mockup's iPad happens to show, and picking the
 * example as the threshold would mean an 80-day-old token is styled as healthy.
 */
export const TOKEN_STALE_AFTER_DAYS = 30

export function tokenIsStale(token: CaptureToken, today: ISODate): boolean {
  if (token.revokedAt) return false
  if (!token.lastUsedAt) return true
  return daysAgoOf(token.lastUsedAt, today) > TOKEN_STALE_AFTER_DAYS
}

// ── The search index ─────────────────────────────────────────────────────────

/** How much of what LifeOS knows about is actually searchable, 0–100. */
export function indexPercent(index: DataStatus['index']): number {
  const total = index.itemsIndexed + index.pending
  if (total === 0) return 0
  return Math.round((index.itemsIndexed / total) * 100)
}

/**
 * The bar goes amber once a twentieth of the corpus is missing — the point at
 * which a search can plausibly miss something the owner remembers writing. Below
 * that the gap is noise and a permanently amber bar would only teach him to
 * ignore it. (The threshold is inferred: the mockups show green at 97% and amber
 * at ~88% and name no rule.)
 */
export function indexTone(index: DataStatus['index']): Exclude<Tone, 'danger'> {
  return indexPercent(index) >= 95 ? 'neutral' : 'warning'
}
